import { useState } from 'react';
import { useImmer } from 'use-immer';
import { useGate, useStore } from '@lib/state-engine';
import { PageTemplate } from '@ui/templates';
import {
  ISimpleMixingBagCardProps,
  SimpleMixingBagCard,
} from '@@/mixing/components/simple-mixing-bag-card';
import { SimpleMixingResultCard } from '@@/mixing/components/simple-mixing-result-card';
import { MixingSummaryAndCalculateCard } from '@@/mixing/components/mixing-summary-and-calculate-card/index.component';
import {
  ISelectBagByWarehouseModalApiProps,
  SelectBagByWarehouseModalApi,
} from '@@/warehouses/components/select-bag-by-warehouse-modal-api';
import {
  ISimpleMixingCustomBagModalProps,
  SimpleMixingCustomBagModal,
} from '@@/mixing/components/simple-mixing-custom-bag-modal';
import { PageGate, $bags } from '@@/mixing/store/simple-mixing.store';
import { $agriculture, $agricultureById } from '@@/agriculture/store/agriculture.store';
import { simpleMix } from '@/entities/simple-mix';
import { ISimpleMixingBag, ISimpleMixingData } from '@@/mixing/ts';
import { IAgriculture } from '@@/agriculture/ts';
import { DiContainer } from '@@/di/di-container';
import { SimpleMixingService } from '@@/mixing/services/simple-mixing.service';
import { useAsyncHandler } from '@@/common/hooks/use-async-handler.hook';
import { useResponseErrorInterceptorHandler } from '@@/common/hooks/use-response-error-interceptor-handler.hook';
import { produce } from 'immer';
import { SIMPLE_MIXING_DEFAULT_BAG_INFO } from '@@/mixing/constants/simple-mixing.constants';

import { SimpleMixingBunkerImage } from '@@/mixing/images';
import styles from './index.module.sass';

const simpleMixingService = DiContainer.get(SimpleMixingService);

export const SimpleMixingPage = () => {
  const agriculture = useStore($agriculture);
  const agricultureById = useStore($agricultureById);
  const mixableAgriculture = agriculture.filter((elem) => elem.mixable);
  useGate(PageGate);

  const { left, right } = useStore($bags);
  const culture = agricultureById[
    (left?.agricultureId as number) ?? (right?.agricultureId as number)
  ] as IAgriculture | undefined;

  const [leftInfo, setLeftBagInfo] = useImmer(
    () =>
      simpleMixingService.getSecondaryDataFromClientStorage()?.[0] || {
        ...SIMPLE_MIXING_DEFAULT_BAG_INFO,
      },
  );
  const [rightInfo, setRightBagInfo] = useImmer(
    () =>
      simpleMixingService.getSecondaryDataFromClientStorage()?.[1] || {
        ...SIMPLE_MIXING_DEFAULT_BAG_INFO,
      },
  );
  const bags = {
    left: {
      bag: left,
      info: leftInfo,
      setInfo: (fn: Parameters<typeof setLeftBagInfo>[0]) => {
        // @ts-ignore
        const newInfo = produce<typeof leftInfo>(leftInfo, fn);
        setLeftBagInfo(() => newInfo);
        simpleMixingService.saveSecondaryDataInClientStorage([newInfo, rightInfo]);
      },
    },
    right: {
      bag: right,
      info: rightInfo,
      setInfo: (fn: Parameters<typeof setLeftBagInfo>[0]) => {
        // @ts-ignore
        const newInfo = produce<typeof rightInfo>(rightInfo, fn);
        setRightBagInfo(() => newInfo);
        simpleMixingService.saveSecondaryDataInClientStorage([leftInfo, newInfo]);
      },
    },
  };

  const bagsWeight = leftInfo.weight + rightInfo.weight;
  const firstBagSummaryPrice = leftInfo.weight * leftInfo.price;
  const secondBagSummaryPrice = rightInfo.weight * rightInfo.price;
  const bagsSummaryPrice = firstBagSummaryPrice + secondBagSummaryPrice;

  const hasEmptyBags = left === null || right === null;
  const [mixPrice, setMixPrice] = useState(0);
  const mixCost = bagsWeight * mixPrice;
  const calculatedProfit = mixCost - bagsSummaryPrice;
  const calculationDisabled =
    hasEmptyBags || leftInfo.weight === 0 || rightInfo.weight === 0;
  const [calculatedError, setCalculatedError] = useState<string | null>(null);

  const [result, setResult] = useImmer<ISimpleMixingData>([]);

  const [selectBagModalOpen, setSelectBagModalOpen] = useState(false);
  const [customBagModalOpen, setCustomBagModalOpen] = useState(false);

  const { isLoading, handler: onCalculate } = useAsyncHandler({
    handler: useResponseErrorInterceptorHandler({
      handler: async () => {
        setCalculatedError(null);

        const response = await simpleMixingService.calculate([
          {
            ...(left?.id ? { id: left.id } : left),
            weight: leftInfo.weight,
          } as ISimpleMixingBag,
          {
            ...(right?.id ? { id: right.id } : right),
            weight: rightInfo.weight,
          } as ISimpleMixingBag,
        ]);

        setResult(() => response.values);
      },
      catchHandler: (err, isResponse, body) => {
        setCalculatedError(isResponse ? body?.message : err.message);
      },
    }),
  });

  const onSelect: (
    // @ts-ignore
    type: Parameters<ISimpleMixingBagCardProps['onSelect']>[0],
    directionType: keyof typeof bags,
    // @ts-ignore
  ) => ReturnType<ISimpleMixingBagCardProps['onSelect']> = (type, directionType) => {
    if (type === 'bag') setSelectBagModalOpen(true);
    if (type === 'custom') setCustomBagModalOpen(true);
  };

  const onSelectBag: ISelectBagByWarehouseModalApiProps['onSelect'] = (bag) => {
    simpleMix.addBag(bag.id);
    setSelectBagModalOpen(false);
  };
  const onSelectManualBag: ISimpleMixingCustomBagModalProps['onAdd'] = (bag) => {
    simpleMix.addBag(bag);
    setCustomBagModalOpen(false);
  };

  const onDelete = (type: keyof typeof bags) => {
    simpleMix.removeBag(
      type === 'left'
        ? (left?.id as string) ?? 'left'
        : (right?.id as string) ?? 'right',
    );

    (type === 'left' ? bags.left.setInfo : bags.right.setInfo)(() => ({
      ...SIMPLE_MIXING_DEFAULT_BAG_INFO,
    }));
  };

  return (
    <PageTemplate
      header="Простое миксование"
      mainClassName={styles.root}
      className={styles.background}
    >
      <div className={styles.bags}>
        <div className={styles.bags__content}>
          {Object.entries(bags).map(([directionType, { bag, info, setInfo }]) => (
            <SimpleMixingBagCard
              key={directionType}
              data={bag}
              price={info.price}
              weight={info.weight}
              onWeightChange={(weight) =>
                setInfo((draft) => void (draft.weight = weight))
              }
              onPriceChange={(price) => setInfo((draft) => void (draft.price = price))}
              onSelect={(type) => onSelect(type, directionType as keyof typeof bags)}
              onDelete={() => onDelete(directionType as keyof typeof bags)}
            />
          ))}
        </div>

        <img className={styles.bags__bg} src={SimpleMixingBunkerImage} alt="" />
      </div>

      <MixingSummaryAndCalculateCard
        summaryPrice={bagsSummaryPrice}
        disabled={calculationDisabled}
        onCalculate={onCalculate}
      />

      <SimpleMixingResultCard
        culture={culture}
        weight={bagsWeight}
        profit={calculatedProfit}
        price={mixPrice}
        cost={mixCost}
        disabled={calculationDisabled || !!calculatedError}
        disabledReason={
          hasEmptyBags
            ? 'Заполните бункеры для миксования'
            : 'Введите корректные параметры миксования'
        }
        onPriceChange={setMixPrice}
        params={result}
        loading={isLoading}
      />

      {selectBagModalOpen && (
        <SelectBagByWarehouseModalApi
          bagFilter={(bag) => {
            if ([left?.id, right?.id].includes(bag.id)) return false;
            if (culture) return bag.agricultureId === culture.id;
            return mixableAgriculture.some((elem) => elem.id === bag.agricultureId);
          }}
          onSelect={onSelectBag}
          onClose={() => setSelectBagModalOpen(false)}
        />
      )}

      {customBagModalOpen && (
        <SimpleMixingCustomBagModal
          agriculture={culture || mixableAgriculture[0]}
          onAdd={onSelectManualBag}
          onClose={() => setCustomBagModalOpen(false)}
        />
      )}
    </PageTemplate>
  );
};
